import {FC, useState, useEffect, useMemo} from 'react'
import {X, Shield, Bot, CheckCircle2, AlertTriangle} from 'lucide-react'
import * as API from '../../api'
import {
  getPermissionDependencies,
  getPermissionRisk,
  isAgentStudioPermission,
  type Permission,
  type PermissionId,
} from './permissionMetadata'
import {SearchInput} from './SearchInput'
import {GroupNameInput} from './GroupNameInput'
import {PermissionList} from './PermissionList'
import {ActionButton} from './ActionButton'

type Props = {
  onChange: Function
  onClose: Function
  data: any
  group?: any
}

const PermissionModal: FC<Props> = (props) => {
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [permissionFilter, setPermissionFilter] = useState<'all' | 'selected' | 'agent-studio'>(
    'all'
  )
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [selectedPermissions, setSelectedPermissions] = useState<PermissionId[]>([])
  const [name, setName] = useState<string>('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!props.data) return
    setPermissions(props.data)
  }, [props.data])

  useEffect(() => {
    if (!props.group) return
    setSelectedPermissions(props.group.data)
    setName(props.group.name)
  }, [props.group])

  const applyPermissionsChange = (targets: Permission[], isChecked: boolean) => {
    setSelectedPermissions((current) => {
      const next = new Set<PermissionId>(current)
      const byName = new Map(permissions.map((permission) => [permission.name, permission]))

      const addPermission = (permission: Permission) => {
        next.add(permission.id_feature)
        getPermissionDependencies(permission.name)
          .map((dependency) => byName.get(dependency))
          .filter((dependency): dependency is Permission => !!dependency)
          .forEach(addPermission)
      }

      const removePermission = (permission: Permission) => {
        next.delete(permission.id_feature)
        permissions.forEach((candidate) => {
          if (
            next.has(candidate.id_feature) &&
            getPermissionDependencies(candidate.name).includes(permission.name)
          ) {
            removePermission(candidate)
          }
        })
      }

      targets.forEach((permission) =>
        isChecked ? addPermission(permission) : removePermission(permission)
      )
      return Array.from(next)
    })
  }

  const handlePermissionChange = (permission: Permission, isChecked: boolean) => {
    applyPermissionsChange([permission], isChecked)
  }

  const getData = () => {
    const obj: any = {
      name: name.trim(),
      data: selectedPermissions,
    }
    if (props.group) obj.id_feature_group = props.group.id_feature_group
    return obj
  }

  const handleSubmit = async () => {
    if (!name.trim() || selectedPermissions.length === 0) return
    setError('')
    setLoading(true)
    try {
      if (props.group) {
        await API.updateGroupPermissions(getData())
      } else {
        await API.addGroupPermissions(getData())
      }
      props.onChange()
    } catch (submitError) {
      console.error('Error:', submitError)
      setError('Não foi possível salvar o grupo. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const selectedSet = useMemo(() => new Set(selectedPermissions), [selectedPermissions])
  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('pt-BR')
  const filteredPermissions = useMemo(
    () =>
      permissions.filter((permission) => {
        const matchesSearch =
          !normalizedSearch ||
          permission.title.toLocaleLowerCase('pt-BR').includes(normalizedSearch) ||
          permission.name.toLowerCase().includes(normalizedSearch)
        const matchesFilter =
          permissionFilter === 'selected'
            ? selectedSet.has(permission.id_feature)
            : permissionFilter === 'agent-studio'
            ? isAgentStudioPermission(permission)
            : true
        return matchesSearch && matchesFilter
      }),
    [permissions, normalizedSearch, permissionFilter, selectedSet]
  )

  const selectedAgentStudioCount = permissions.filter(
    (permission) => isAgentStudioPermission(permission) && selectedSet.has(permission.id_feature)
  ).length
  const sensitiveSelectedCount = permissions.filter(
    (permission) =>
      getPermissionRisk(permission.name) !== 'standard' && selectedSet.has(permission.id_feature)
  ).length

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-2 backdrop-blur-sm sm:p-4'
      role='dialog'
      aria-modal='true'
    >
      <div className='animate-scale-up max-h-[94vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-xl'>
        {/* Header */}
        <div className='flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4'>
          <div className='flex items-center gap-3'>
            <div className='rounded-lg bg-white/10 p-2'>
              <Shield className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>
                {props.group ? 'Editar grupo de permissões' : 'Novo grupo de permissões'}
              </h2>
              <p className='mt-0.5 text-xs text-blue-100'>
                Organize acessos por sistema, tela e nível de risco.
              </p>
            </div>
          </div>
          <button
            onClick={() => props.onClose()}
            className='text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors'
            type='button'
            aria-label='Fechar modal'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex h-[calc(94vh-4.5rem)] flex-col'>
          <div className='border-b border-gray-200 bg-white px-5 py-4 sm:px-7'>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              <GroupNameInput value={name} onChange={setName} />
              <SearchInput value={searchTerm} onChange={setSearchTerm} />
            </div>
            <div className='mt-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
              <div className='flex flex-wrap gap-2'>
                <button
                  type='button'
                  onClick={() => setPermissionFilter('all')}
                  className={
                    'rounded-lg px-3 py-2 text-xs font-semibold transition ' +
                    (permissionFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                  }
                >
                  Todas
                </button>
                <button
                  type='button'
                  onClick={() => setPermissionFilter('selected')}
                  className={
                    'rounded-lg px-3 py-2 text-xs font-semibold transition ' +
                    (permissionFilter === 'selected'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                  }
                >
                  Selecionadas ({selectedPermissions.length})
                </button>
                <button
                  type='button'
                  onClick={() => setPermissionFilter('agent-studio')}
                  className={
                    'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ' +
                    (permissionFilter === 'agent-studio'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100')
                  }
                >
                  <Bot className='h-4 w-4' /> Agent Studio ({selectedAgentStudioCount})
                </button>
              </div>
              <div className='flex flex-wrap items-center gap-2 text-xs'>
                <span className='inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 font-semibold text-green-700'>
                  <CheckCircle2 className='h-4 w-4' /> {selectedPermissions.length} de{' '}
                  {permissions.length} selecionadas
                </span>
                {sensitiveSelectedCount > 0 && (
                  <span className='inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 font-semibold text-amber-700'>
                    <AlertTriangle className='h-4 w-4' /> {sensitiveSelectedCount} sensíveis ou
                    críticas
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable Permission List */}
          <div className='custom-scrollbar flex-1 overflow-y-auto bg-gray-50/70 px-4 py-4 sm:px-7'>
            <PermissionList
              permissions={filteredPermissions}
              allPermissions={permissions}
              selectedPermissions={selectedPermissions}
              onPermissionChange={handlePermissionChange}
              onPermissionsChange={applyPermissionsChange}
            />
          </div>

          {/* Footer */}
          <div className='flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7'>
            <div>
              <p className={'text-xs ' + (error ? 'font-semibold text-red-600' : 'text-gray-500')}>
                {error ||
                  'Informe um nome e selecione pelo menos uma permissão para salvar o grupo.'}
              </p>
            </div>
            <div className='flex flex-col-reverse gap-2 sm:flex-row sm:items-center'>
              <button
                type='button'
                onClick={() => props.onClose()}
                disabled={loading}
                className='rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50'
              >
                Cancelar
              </button>
              <div className='w-full sm:w-64'>
                <ActionButton
                  loading={loading}
                  disabled={loading || !name.trim() || selectedPermissions.length === 0}
                  isEdit={!!props.group}
                  onClick={handleSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-scale-up {
          animation: scaleUp 0.2s ease-out;
        }

        @keyframes scaleUp {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #CBD5E1 transparent;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #CBD5E1;
          border-radius: 3px;
        }
      `}</style>
    </div>
  )
}

export default PermissionModal
